/**
 * Agent Builder — cockpit page shell (Phase 2, T2.5).
 *
 * Hosts the four cockpit views (Library / Build / Run / Telemetry) as tabs and
 * threads shared state between them: the Library opens a workflow into Build or Run,
 * and RunView surfaces its active run id to TelemetryView. Resolves the current user
 * from the app store and passes it to the views (which forward X-User-Email).
 *
 * The cockpit is reachable ONLY when VITE_ENABLE_AGENT_BUILDER is on — the route
 * that renders this page is registered behind that flag in App.tsx, and this shell
 * additionally guards itself. With the flag OFF nothing here renders. Additive:
 * reuses the existing UI primitives.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { LayoutGrid, PencilRuler, Play, BarChart3 } from 'lucide-react';
import { useAppStore } from '../../stores/appStore';
import LibraryView from './LibraryView';
import BuildView, { NEW_WORKFLOW_SENTINEL } from './BuildView';
import RunView from './RunView';
import TelemetryView from './TelemetryView';
import type { RunEventFrame } from '../../types/agentBuilder';

/** Mirrors the backend `enable_agent_builder` flag (default OFF). */
export const AGENT_BUILDER_FLAG =
  String(import.meta.env.VITE_ENABLE_AGENT_BUILDER ?? '').toLowerCase() === 'true';

type Tab = 'library' | 'build' | 'run' | 'telemetry';

const TABS: { id: Tab; label: string; icon: React.ElementType }[] = [
  { id: 'library', label: 'Library', icon: LayoutGrid },
  { id: 'build', label: 'Build', icon: PencilRuler },
  { id: 'run', label: 'Run', icon: Play },
  { id: 'telemetry', label: 'Telemetry', icon: BarChart3 },
];

export const AgentBuilderPage: React.FC = () => {
  const currentUser = useAppStore((s) => s.currentUser);
  const userEmail = currentUser?.email;
  const setSidebarCollapsed = useAppStore((s) => s.setSidebarCollapsed);
  const sidebarCollapsed = useAppStore((s) => s.sidebarCollapsed);

  const [tab, setTab] = useState<Tab>('library');
  const [buildWorkflowId, setBuildWorkflowId] = useState<string | undefined>(undefined);
  const [runWorkflowId, setRunWorkflowId] = useState<string | undefined>(undefined);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [wasCollapsed, setWasCollapsed] = useState<boolean | null>(null);
  // Lift the run's SSE frames here so (a) TelemetryView can populate guardrail KPIs
  // from them and (b) run state survives a tab switch (RunView keeps its own canvas
  // state, but the page-level events feed Telemetry independently of which tab is
  // mounted).
  const [runEvents, setRunEvents] = useState<RunEventFrame[]>([]);

  const handleRunStarted = useCallback((runId: string) => {
    setActiveRunId(runId);
    setRunEvents([]); // fresh run -> reset collected frames
  }, []);
  const handleRunEvents = useCallback((events: RunEventFrame[]) => {
    setRunEvents(events);
  }, []);

  // Auto-collapse the primary nav while the cockpit is open (more canvas room),
  // and restore the user's prior state on exit — mirrors AIAssistantPage.
  useEffect(() => {
    if (wasCollapsed === null) setWasCollapsed(sidebarCollapsed);
    if (!sidebarCollapsed) setSidebarCollapsed(true);
    return () => {
      if (wasCollapsed !== null && wasCollapsed !== sidebarCollapsed) {
        setSidebarCollapsed(wasCollapsed);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Guard: the route is flag-gated in App.tsx, but defend against direct mount too.
  if (!AGENT_BUILDER_FLAG) return null;

  const openInBuild = (workflowId: string) => {
    setBuildWorkflowId(workflowId);
    setTab('build');
  };
  const openInRun = (workflowId: string) => {
    setRunWorkflowId(workflowId);
    setTab('run');
  };
  const openNewInBuild = () => {
    setBuildWorkflowId(NEW_WORKFLOW_SENTINEL);
    setTab('build');
  };

  const body = useMemo(() => {
    switch (tab) {
      case 'library':
        return <LibraryView onOpen={openInBuild} onRun={openInRun} onNew={openNewInBuild} />;
      case 'build':
        return <BuildView key={buildWorkflowId ?? 'new'} workflowId={buildWorkflowId} currentUser={userEmail} />;
      case 'run':
        return (
          <RunView
            key={runWorkflowId ?? 'run'}
            workflowId={runWorkflowId}
            currentUser={userEmail}
            onRunStarted={handleRunStarted}
            onEvents={handleRunEvents}
          />
        );
      case 'telemetry':
        return <TelemetryView runId={activeRunId} events={runEvents} />;
      default:
        return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, buildWorkflowId, runWorkflowId, activeRunId, runEvents, userEmail, handleRunStarted, handleRunEvents]);

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col" data-testid="agent-builder-page">
      <header className="border-b border-neutral-200 bg-white px-6 pt-4">
        <h1 className="text-xl font-bold text-neutral-900">Agent Builder</h1>
        <p className="mb-3 text-sm text-neutral-500">
          Compose, run and govern agent workflows — agents power schema.
        </p>
        <nav className="flex gap-1" role="tablist" aria-label="Agent Builder views">
          {TABS.map((t) => {
            const active = t.id === tab;
            return (
              <button
                key={t.id}
                role="tab"
                aria-selected={active}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 rounded-t-lg px-4 py-2 text-sm font-medium transition-colors ${
                  active
                    ? 'border-b-2 border-secondary-500 text-secondary-700'
                    : 'text-neutral-500 hover:text-neutral-800'
                }`}
              >
                <t.icon className="h-4 w-4" />
                {t.label}
              </button>
            );
          })}
        </nav>
      </header>
      <div className="min-h-0 flex-1 overflow-hidden">{body}</div>
    </div>
  );
};

export default AgentBuilderPage;
